/**
 * User Management Page - Admin Only
 * Full CRUD: list, create, edit role, toggle status, delete
 */
import React, { useState, useEffect } from 'react';
import {
  Users, Shield, Search, Mail, Clock, CheckCircle2, XCircle,
  Loader2, UserPlus, Trash2, Power, X, Eye, EyeOff,
  BarChart3
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { useToast } from '../../hooks/use-toast';
import { apiFetch } from '../../services/api_service';


const ROLE_COLORS = {
  admin: { bg: 'bg-red-500/20', text: 'text-red-400', border: 'border-red-500/30' },
  preparer: { bg: 'bg-blue-500/20', text: 'text-blue-400', border: 'border-blue-500/30' },
  reviewer: { bg: 'bg-purple-500/20', text: 'text-purple-400', border: 'border-purple-500/30' },
  approver: { bg: 'bg-emerald-500/20', text: 'text-emerald-400', border: 'border-emerald-500/30' },
  auditor: { bg: 'bg-amber-500/20', text: 'text-amber-400', border: 'border-amber-500/30' },
  verifier: { bg: 'bg-cyan-500/20', text: 'text-cyan-400', border: 'border-cyan-500/30' },
  viewer: { bg: 'bg-[#1F1F1F]', text: 'text-[#A0A0A0]', border: 'border-[#2A2A2A]' },
};

// Must match backend/routes/admin_routes.py VALID_ROLES (ABAC vocabulary)
const VALID_ROLES = ['admin', 'preparer', 'reviewer', 'approver', 'auditor', 'verifier', 'viewer'];

export function UserManagementPage() {
  const { toast } = useToast();
  const [users, setUsers] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [editingUser, setEditingUser] = useState(null);
  const [showCreate, setShowCreate] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(null);

  const getToken = () => localStorage.getItem('token');

  const fetchUsers = async () => {
    try {
      const data = await apiFetch(`/v1/admin/users`, {
        headers: { 'Authorization': `Bearer ${getToken()}` },
      });
      if (data && !data._error) {
        setUsers(data.users || []);
      }
    } catch (e) {
      console.error('Failed to fetch users:', e);
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const res = await apiFetch(`/v1/admin/users/stats`, {
        headers: { 'Authorization': `Bearer ${getToken()}` },
      });
      if (res && !res._error) setStats(res);
    } catch (e) { /* ignore */ }
  };

  useEffect(() => { fetchUsers(); fetchStats(); }, []);

  const updateRole = async (userId, newRole) => {
    try {
      const res = await apiFetch(`/v1/admin/users/${userId}/role`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${getToken()}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ role: newRole }),
      });
      if (res && !res._error) {
        toast({ title: 'Role Updated', description: `User role changed to ${newRole}` });
        fetchUsers();
        fetchStats();
      } else {
        toast({ title: 'Update Failed', description: res?.message, variant: 'destructive' });
      }
    } catch {
      toast({ title: 'Error', description: 'Failed to update role', variant: 'destructive' });
    }
    setEditingUser(null);
  };

  const toggleStatus = async (userId, currentStatus) => {
    const newStatus = currentStatus === 'inactive' ? 'active' : 'inactive';
    try {
      const res = await apiFetch(`/v1/admin/users/${userId}/status`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${getToken()}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ status: newStatus }),
      });
      if (res && !res._error) {
        toast({ title: 'Status Updated', description: `User ${newStatus === 'active' ? 'activated' : 'deactivated'}` });
        fetchUsers();
        fetchStats();
      }
    } catch {
      toast({ title: 'Error', description: 'Failed to update status', variant: 'destructive' });
    }
  };

  const deleteUser = async (userId) => {
    try {
      const res = await apiFetch(`/v1/admin/users/${userId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${getToken()}` },
      });
      if (res && !res._error) {
        toast({ title: 'User Deleted' });
        fetchUsers();
        fetchStats();
      } else {
        toast({ title: 'Delete Failed', description: res?.message, variant: 'destructive' });
      }
    } catch {
      toast({ title: 'Error', description: 'Failed to delete user', variant: 'destructive' });
    }
    setConfirmDelete(null);
  };

  const filteredUsers = users.filter(u =>
    !search || u.email?.toLowerCase().includes(search.toLowerCase()) ||
    u.full_name?.toLowerCase().includes(search.toLowerCase()) ||
    u.role?.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div className="space-y-6" data-testid="user-management-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white">User Management</h1>
          <p className="text-sm text-[#6B6B6B] mt-1">Manage platform users and access roles</p>
        </div>
        <Button
          className="bg-emerald-600 hover:bg-emerald-700 text-white gap-1.5"
          onClick={() => setShowCreate(true)}
          data-testid="invite-user-btn"
        >
          <UserPlus className="w-4 h-4" /> Invite User
        </Button>
      </div>

      {/* Stats Dashboard */}
      {stats && (
        <div className="grid grid-cols-4 gap-4" data-testid="user-stats">
          <StatCard icon={Users} color="emerald" label="Total Users" value={stats.total} />
          <StatCard icon={CheckCircle2} color="blue" label="Active" value={stats.active} />
          <StatCard icon={XCircle} color="red" label="Inactive" value={stats.inactive} />
          <StatCard icon={BarChart3} color="purple" label="Roles" value={Object.keys(stats.roleBreakdown || {}).length} />
        </div>
      )}

      {/* Role Distribution */}
      {stats?.roleBreakdown && Object.keys(stats.roleBreakdown).length > 0 && (
        <div className="flex items-center gap-3 flex-wrap" data-testid="role-distribution">
          {Object.entries(stats.roleBreakdown).map(([role, count]) => {
            const rc = ROLE_COLORS[role] || ROLE_COLORS.viewer;
            return (
              <div key={role} className={`flex items-center gap-2 px-3 py-1.5 rounded-lg ${rc.bg} border ${rc.border}`}>
                <Shield className={`w-3 h-3 ${rc.text}`} />
                <span className={`text-xs font-medium ${rc.text}`}>{role}: {count}</span>
              </div>
            );
          })}
        </div>
      )}

      {/* Search */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#6B6B6B]" />
        <input
          type="text"
          placeholder="Search by name, email, or role..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="w-full bg-[#111111] border border-[#1F1F1F] rounded-lg pl-10 pr-4 py-2.5 text-sm text-white placeholder-[#6B6B6B] focus:border-emerald-500/50 focus:outline-none"
          data-testid="user-search-input"
        />
      </div>

      {/* Users Table */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-6 h-6 text-emerald-400 animate-spin" />
        </div>
      ) : (
        <div className="bg-[#111111] border border-[#1F1F1F] rounded-xl overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full" data-testid="users-table">
              <thead>
                <tr className="border-b border-[#1F1F1F]">
                  <th className="px-4 py-3 text-left text-xs font-medium text-[#6B6B6B]">User</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-[#6B6B6B]">Role</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-[#6B6B6B]">Status</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-[#6B6B6B]">Joined</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-[#6B6B6B]">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[#1F1F1F]">
                {filteredUsers.map((user, i) => {
                  const roleStyle = ROLE_COLORS[user.role] || ROLE_COLORS.viewer;
                  const userId = user.user_id || user.id || user.email;
                  const isInactive = user.status === 'inactive' || user.is_active === false;
                  return (
                    <tr key={i} className="hover:bg-[#151515]" data-testid={`user-row-${i}`}>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-3">
                          <div className={`w-8 h-8 rounded-full ${isInactive ? 'bg-[#1F1F1F]' : 'bg-emerald-500/20'} flex items-center justify-center ${isInactive ? 'text-[#4A4A4A]' : 'text-emerald-400'} text-xs font-bold`}>
                            {(user.full_name || user.email || '?')[0].toUpperCase()}
                          </div>
                          <div>
                            <p className={`text-sm ${isInactive ? 'text-[#6B6B6B]' : 'text-white'}`}>{user.full_name || user.email}</p>
                            <p className="text-xs text-[#6B6B6B] flex items-center gap-1">
                              <Mail className="w-3 h-3" />{user.email}
                            </p>
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        {editingUser === userId ? (
                          <select
                            defaultValue={user.role || 'viewer'}
                            onChange={(e) => updateRole(userId, e.target.value)}
                            onBlur={() => setEditingUser(null)}
                            autoFocus
                            className="bg-[#0D0D0D] border border-emerald-500/50 rounded px-2 py-1 text-xs text-white"
                            data-testid={`role-select-${i}`}
                          >
                            {VALID_ROLES.map(r => (
                              <option key={r} value={r}>{r}</option>
                            ))}
                          </select>
                        ) : (
                          <Badge
                            className={`${roleStyle.bg} ${roleStyle.text} border ${roleStyle.border} cursor-pointer`}
                            onClick={() => setEditingUser(userId)}
                            data-testid={`role-badge-${i}`}
                          >
                            <Shield className="w-3 h-3 mr-1" />
                            {user.role || 'viewer'}
                          </Badge>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-1">
                          {!isInactive ? (
                            <CheckCircle2 className="w-3 h-3 text-emerald-400" />
                          ) : (
                            <XCircle className="w-3 h-3 text-red-400" />
                          )}
                          <span className={`text-xs ${!isInactive ? 'text-emerald-400' : 'text-red-400'}`}>
                            {!isInactive ? 'Active' : 'Inactive'}
                          </span>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-xs text-[#6B6B6B]">
                        {user.created_at ? new Date(user.created_at).toLocaleDateString() : '-'}
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center justify-end gap-1">
                          <button
                            onClick={() => setEditingUser(userId)}
                            className="p-1.5 rounded-lg text-[#6B6B6B] hover:text-white hover:bg-[#1F1F1F] transition-colors"
                            title="Edit Role"
                            data-testid={`edit-role-${i}`}
                          >
                            <Shield className="w-3.5 h-3.5" />
                          </button>
                          <button
                            onClick={() => toggleStatus(userId, isInactive ? 'inactive' : 'active')}
                            className={`p-1.5 rounded-lg transition-colors ${isInactive ? 'text-emerald-400 hover:bg-emerald-500/10' : 'text-amber-400 hover:bg-amber-500/10'}`}
                            title={isInactive ? 'Activate' : 'Deactivate'}
                            data-testid={`toggle-status-${i}`}
                          >
                            <Power className="w-3.5 h-3.5" />
                          </button>
                          <button
                            onClick={() => setConfirmDelete(userId)}
                            className="p-1.5 rounded-lg text-[#6B6B6B] hover:text-red-400 hover:bg-red-500/10 transition-colors"
                            title="Delete"
                            data-testid={`delete-user-${i}`}
                          >
                            <Trash2 className="w-3.5 h-3.5" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          {filteredUsers.length === 0 && (
            <div className="py-10 text-center text-sm text-[#6B6B6B]">No users found</div>
          )}
        </div>
      )}

      {/* Create User Modal */}
      {showCreate && (
        <CreateUserModal
          onClose={() => setShowCreate(false)}
          onCreated={() => { setShowCreate(false); fetchUsers(); fetchStats(); }}
        />
      )}

      {/* Confirm Delete Modal */}
      {confirmDelete && (
        <div className="fixed inset-0 z-50 bg-black/75 backdrop-blur-sm flex items-center justify-center p-4" data-testid="delete-confirm-modal">
          <div className="bg-[#0C0C0C] border border-[#1F1F1F] rounded-2xl p-6 max-w-sm w-full">
            <h3 className="text-lg font-bold text-white mb-2">Delete User?</h3>
            <p className="text-sm text-[#6B6B6B] mb-4">This action cannot be undone. The user will be permanently removed.</p>
            <div className="flex gap-2 justify-end">
              <Button variant="outline" size="sm" className="border-[#2A2A2A] text-[#A0A0A0]" onClick={() => setConfirmDelete(null)}>Cancel</Button>
              <Button size="sm" className="bg-red-600 hover:bg-red-700 text-white" onClick={() => deleteUser(confirmDelete)} data-testid="confirm-delete-btn">Delete</Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function StatCard({ icon: Icon, color, label, value }) {
  return (
    <div className="bg-[#111111] border border-[#1F1F1F] rounded-xl p-4">
      <div className="flex items-center gap-3">
        <div className={`p-2 rounded-lg bg-${color}-500/20`}>
          <Icon size={18} className={`text-${color}-400`} />
        </div>
        <div>
          <div className="text-2xl font-bold text-white">{value}</div>
          <div className="text-xs text-[#6B6B6B]">{label}</div>
        </div>
      </div>
    </div>
  );
}

function CreateUserModal({ onClose, onCreated }) {
  const { toast } = useToast();
  const [form, setForm] = useState({ email: '', full_name: '', role: 'viewer', password: '' });
  const [saving, setSaving] = useState(false);
  const [showPwd, setShowPwd] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const res = await apiFetch(`/v1/admin/users`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(form),
      });
      if (res && !res._error) {
        toast({ title: 'User Created', description: `${form.email} has been invited` });
        onCreated();
      } else {
        toast({ title: 'Failed', description: res?.message, variant: 'destructive' });
      }
    } catch {
      toast({ title: 'Error', description: 'Failed to create user', variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/75 backdrop-blur-sm flex items-center justify-center p-4" data-testid="create-user-modal">
      <div className="bg-[#0C0C0C] border border-[#1F1F1F] rounded-2xl max-w-md w-full overflow-hidden">
        <div className="border-b border-[#1F1F1F] px-6 py-4 flex items-center justify-between">
          <h2 className="text-lg font-bold text-white flex items-center gap-2">
            <UserPlus className="w-5 h-5 text-emerald-400" /> Invite New User
          </h2>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-[#1F1F1F]">
            <X className="w-4 h-4 text-[#6B6B6B]" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div>
            <label className="block text-xs font-medium text-[#A0A0A0] mb-1.5">Email *</label>
            <input
              type="email"
              required
              value={form.email}
              onChange={e => setForm(p => ({ ...p, email: e.target.value }))}
              className="w-full bg-[#111] border border-[#2A2A2A] rounded-lg px-3 py-2.5 text-sm text-white placeholder-[#4A4A4A] focus:border-emerald-500/50 focus:outline-none"
              placeholder="user@company.com"
              data-testid="create-email"
            />
          </div>

          <div>
            <label className="block text-xs font-medium text-[#A0A0A0] mb-1.5">Full Name</label>
            <input
              type="text"
              value={form.full_name}
              onChange={e => setForm(p => ({ ...p, full_name: e.target.value }))}
              className="w-full bg-[#111] border border-[#2A2A2A] rounded-lg px-3 py-2.5 text-sm text-white placeholder-[#4A4A4A] focus:border-emerald-500/50 focus:outline-none"
              placeholder="John Doe"
              data-testid="create-name"
            />
          </div>

          <div>
            <label className="block text-xs font-medium text-[#A0A0A0] mb-1.5">Role</label>
            <select
              value={form.role}
              onChange={e => setForm(p => ({ ...p, role: e.target.value }))}
              className="w-full bg-[#111] border border-[#2A2A2A] rounded-lg px-3 py-2.5 text-sm text-white focus:border-emerald-500/50 focus:outline-none"
              data-testid="create-role"
            >
              {VALID_ROLES.map(r => (
                <option key={r} value={r}>{r.charAt(0).toUpperCase() + r.slice(1)}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-xs font-medium text-[#A0A0A0] mb-1.5">Password *</label>
            <div className="relative">
              <input
                type={showPwd ? 'text' : 'password'}
                required
                minLength={8}
                value={form.password}
                onChange={e => setForm(p => ({ ...p, password: e.target.value }))}
                className="w-full bg-[#111] border border-[#2A2A2A] rounded-lg px-3 py-2.5 pr-10 text-sm text-white placeholder-[#4A4A4A] focus:border-emerald-500/50 focus:outline-none"
                placeholder="Minimum 8 characters"
                data-testid="create-password"
              />
              <button type="button" onClick={() => setShowPwd(!showPwd)} className="absolute right-3 top-1/2 -translate-y-1/2 text-[#6B6B6B]">
                {showPwd ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
          </div>

          <div className="flex gap-2 justify-end pt-2">
            <Button type="button" variant="outline" size="sm" className="border-[#2A2A2A] text-[#A0A0A0]" onClick={onClose}>Cancel</Button>
            <Button type="submit" size="sm" disabled={saving} className="bg-emerald-600 hover:bg-emerald-700 text-white gap-1.5 disabled:opacity-50" data-testid="submit-create-user">
              {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <UserPlus className="w-3.5 h-3.5" />}
              {saving ? 'Creating...' : 'Create User'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default UserManagementPage;
